import type { TFunction } from "i18next";
import type {
	BenchmarkClassificationFacetFieldsFragment,
	BenchmarkCommunityFieldsFragment,
	BenchmarkFirmFieldsFragment,
	BenchmarkReportingUnitFieldsFragment,
	BenchmarkRosterEntryFieldsFragment,
	BenchmarkSubmissionFieldsFragment,
	GetBenchmarkSubmissionsQueryVariables
} from "~/gql/queries/benchmark.generated";

import { isAfter } from "date-fns/isAfter";
import { isBefore } from "date-fns/isBefore";
import { isEqual } from "date-fns/isEqual";
import { isValid } from "date-fns/isValid";
import { parseISO } from "date-fns/parseISO";

import {
	BenchmarkCommunitySegment,
	BenchmarkReportingCadence,
	BenchmarkSubmissionStatus
} from "~/gql/graphqlBase.generated";
import { toGraphqlErrorEntries } from "~/helpers/rtkErrorClassification";

export const BENCHMARK_FIRM_NOT_REGISTERED_CODE = "BENCHMARK_FIRM_NOT_REGISTERED";
export const BENCHMARK_SUBMISSIONS_PAGE_SIZE = 200;

export type SubmissionStatusChipColor = "default" | "info" | "success" | "warning";

export type BenchmarkHomeState =
	| { communities: BenchmarkCommunityFieldsFragment[]; firms: BenchmarkFirmFieldsFragment[]; kind: "ready" }
	| { error: unknown; kind: "error" }
	| { kind: "empty" };

export interface BenchmarkHomeQuerySnapshot {
	communitiesData: BenchmarkCommunityFieldsFragment[] | undefined;
	communitiesError: unknown;
	firmsData: BenchmarkFirmFieldsFragment[] | undefined;
	firmsError: unknown;
}

export interface SubmissionDisplayRow {
	firmLabel: string;
	periodId: string;
	reportingUnitLabel: string;
	status: BenchmarkSubmissionStatus;
	statusChipColor: SubmissionStatusChipColor;
	statusLabelKey: string;
	submissionId: string;
	version: number;
}

export type BenchmarkSubmissionsScopeArgs = Pick<
	GetBenchmarkSubmissionsQueryVariables,
	"communityId" | "periodId" | "reportingUnitId"
>;

const SUBMISSION_STATUS_LABEL_KEYS: Record<BenchmarkSubmissionStatus, string> = {
	[BenchmarkSubmissionStatus.Approved]: "status.approved",
	[BenchmarkSubmissionStatus.Draft]: "status.draft",
	[BenchmarkSubmissionStatus.InReview]: "status.inReview",
	[BenchmarkSubmissionStatus.Queried]: "status.queried",
	[BenchmarkSubmissionStatus.Submitted]: "status.submitted",
	[BenchmarkSubmissionStatus.Superseded]: "status.superseded"
};

const SUBMISSION_STATUS_CHIP_COLORS: Record<BenchmarkSubmissionStatus, SubmissionStatusChipColor> = {
	[BenchmarkSubmissionStatus.Approved]: "success",
	[BenchmarkSubmissionStatus.Draft]: "default",
	[BenchmarkSubmissionStatus.InReview]: "info",
	[BenchmarkSubmissionStatus.Queried]: "warning",
	[BenchmarkSubmissionStatus.Submitted]: "info",
	[BenchmarkSubmissionStatus.Superseded]: "default"
};

const REPORTING_CADENCE_LABEL_KEYS: Record<BenchmarkReportingCadence, string> = {
	[BenchmarkReportingCadence.Cycle]: "cadence.cycle",
	[BenchmarkReportingCadence.Month]: "cadence.month"
};

const COMMUNITY_SEGMENT_LABEL_KEYS: Record<BenchmarkCommunitySegment, string> = {
	[BenchmarkCommunitySegment.Grower]: "segment.grower",
	[BenchmarkCommunitySegment.Retail]: "segment.retail"
};

const CLASSIFICATION_FACET_KEY_LABELS: Record<string, string> = {
	acreage: "firms.facetKeys.acreage",
	channel: "firms.facetKeys.channel",
	climate: "firms.facetKeys.climate",
	crop: "firms.facetKeys.crop",
	market: "firms.facetKeys.market",
	production: "firms.facetKeys.production",
	region: "firms.facetKeys.region",
	salesband: "firms.facetKeys.salesband",
	segment: "firms.facetKeys.segment",
	size: "firms.facetKeys.size",
	tier: "firms.facetKeys.tier",
	type: "firms.facetKeys.type"
};

const CLASSIFICATION_FACET_VALUE_LABELS: Record<string, string> = {
	band1: "firms.facetValues.band1",
	band2: "firms.facetValues.band2",
	band3: "firms.facetValues.band3",
	band4: "firms.facetValues.band4",
	central: "firms.facetValues.central",
	container: "firms.facetValues.container",
	east: "firms.facetValues.east",
	field: "firms.facetValues.field",
	greenhouse: "firms.facetValues.greenhouse",
	midwest: "firms.facetValues.midwest",
	mountain: "firms.facetValues.mountain",
	multistore: "firms.facetValues.multistore",
	north: "firms.facetValues.north",
	northeast: "firms.facetValues.northeast",
	northwest: "firms.facetValues.northwest",
	pacific: "firms.facetValues.pacific",
	retail: "firms.facetValues.retail",
	singlestore: "firms.facetValues.singlestore",
	south: "firms.facetValues.south",
	southeast: "firms.facetValues.southeast",
	southwest: "firms.facetValues.southwest",
	west: "firms.facetValues.west",
	wholesale: "firms.facetValues.wholesale"
};

export const isBenchmarkFirmNotRegisteredError = (error: unknown): boolean => {
	if (!error) return false;
	const entries = toGraphqlErrorEntries(error);
	if (!entries || entries.length === 0) return false;
	return entries.every((entry) => entry.extensions?.code === BENCHMARK_FIRM_NOT_REGISTERED_CODE);
};

export const resolveBenchmarkHomeState = ({
	communitiesData,
	communitiesError,
	firmsData,
	firmsError
}: BenchmarkHomeQuerySnapshot): BenchmarkHomeState => {
	if (communitiesError) {
		if (isBenchmarkFirmNotRegisteredError(communitiesError)) {
			return { kind: "empty" };
		}
		return { error: communitiesError, kind: "error" };
	}

	if (firmsError && !isBenchmarkFirmNotRegisteredError(firmsError)) {
		return { error: firmsError, kind: "error" };
	}

	const communities = communitiesData ?? [];
	if (communities.length === 0) {
		return { kind: "empty" };
	}

	const firms = firmsError ? [] : (firmsData ?? []);
	return { communities, firms, kind: "ready" };
};

export const isRosterEntryEligible = (entry: BenchmarkRosterEntryFieldsFragment, asOfIso?: string): boolean => {
	if (!entry.isEffective) return false;
	if (!asOfIso) return true;
	const asOfDate = parseISO(asOfIso);
	if (!isValid(asOfDate)) return true;
	const startsAtDate = parseISO(entry.startsAt);
	if (isValid(startsAtDate) && isAfter(startsAtDate, asOfDate)) return false;
	if (entry.endsAt) {
		const endsAtDate = parseISO(entry.endsAt);
		if (isValid(endsAtDate) && (isBefore(endsAtDate, asOfDate) || isEqual(endsAtDate, asOfDate))) {
			return false;
		}
	}
	return true;
};

export const filterEligibleRosterEntries = (
	roster: readonly BenchmarkRosterEntryFieldsFragment[],
	asOfIso?: string
): BenchmarkRosterEntryFieldsFragment[] => roster.filter((entry) => isRosterEntryEligible(entry, asOfIso));

export const submissionStatusLabelKey = (status: BenchmarkSubmissionStatus): string =>
	SUBMISSION_STATUS_LABEL_KEYS[status];

export const submissionStatusChipColor = (status: BenchmarkSubmissionStatus): SubmissionStatusChipColor =>
	SUBMISSION_STATUS_CHIP_COLORS[status];

export const reportingCadenceLabelKey = (cadence: BenchmarkReportingCadence): string =>
	REPORTING_CADENCE_LABEL_KEYS[cadence];

export const communitySegmentLabelKey = (segment: BenchmarkCommunitySegment): string =>
	COMMUNITY_SEGMENT_LABEL_KEYS[segment];

export const formatReportingUnitChipLabel = (
	unit: BenchmarkReportingUnitFieldsFragment,
	t: TFunction<"benchmark">
): string =>
	t("firms.reportingUnitChip", {
		cadence: t(reportingCadenceLabelKey(unit.cadence)),
		label: unit.label
	});

export const formatClassificationFacetLabel = (
	facet: BenchmarkClassificationFacetFieldsFragment,
	t: TFunction<"benchmark">
): string => {
	const normalizedKey = facet.key.trim().toLowerCase();
	const normalizedValue = facet.value.trim().toLowerCase();
	const valueLabelKey = CLASSIFICATION_FACET_VALUE_LABELS[normalizedValue];
	const displayValue = valueLabelKey ? t(valueLabelKey) : facet.value.trim();
	const keyLabelKey = CLASSIFICATION_FACET_KEY_LABELS[normalizedKey];
	if (!keyLabelKey) {
		return displayValue;
	}
	return t("firms.facetWithLabel", {
		label: t(keyLabelKey),
		value: displayValue
	});
};

export const serializeBenchmarkSubmissionsQueryKey = (
	endpointName: string,
	queryArgs: BenchmarkSubmissionsScopeArgs
): string =>
	`${endpointName}-${JSON.stringify({
		communityId: queryArgs.communityId,
		periodId: queryArgs.periodId ?? null,
		reportingUnitId: queryArgs.reportingUnitId ?? null
	})}`;

export const mergeBenchmarkSubmissionPages = <T extends { submissionId: string }>(
	loaded: readonly T[],
	incoming: readonly T[],
	isFirstPage: boolean
): T[] => {
	if (isFirstPage) return [...incoming];
	if (incoming.length === 0) return [...loaded];
	const incomingById = new Map(incoming.map((submission) => [submission.submissionId, submission]));
	const seen = new Set(loaded.map((submission) => submission.submissionId));
	const refreshed = loaded.map((submission) => incomingById.get(submission.submissionId) ?? submission);
	const added = incoming.filter((submission) => !seen.has(submission.submissionId));
	return [...refreshed, ...added];
};

export const selectVisibleSubmissions = (
	submissions: readonly BenchmarkSubmissionFieldsFragment[],
	community: BenchmarkCommunityFieldsFragment,
	myFirms: readonly BenchmarkFirmFieldsFragment[]
): BenchmarkSubmissionFieldsFragment[] => {
	const communityFirmIds = new Set(community.firms.map((firm) => firm.firmId));
	const signedInCommunityFirmIds = new Set(
		myFirms.filter((firm) => communityFirmIds.has(firm.firmId)).map((firm) => firm.firmId)
	);
	if (signedInCommunityFirmIds.size === 0) {
		return [...submissions];
	}
	return submissions.filter((submission) => signedInCommunityFirmIds.has(submission.firmId));
};

export const resolveSubmissionDisplayRow = (
	submission: BenchmarkSubmissionFieldsFragment,
	community: BenchmarkCommunityFieldsFragment
): SubmissionDisplayRow => {
	const firm = community.firms.find((entry) => entry.firmId === submission.firmId);
	const reportingUnit = firm?.reportingUnits.find((unit) => unit.reportingUnitId === submission.reportingUnitId);
	return {
		firmLabel: firm?.label ?? submission.firmId,
		periodId: submission.periodId,
		reportingUnitLabel: reportingUnit?.label ?? submission.reportingUnitId,
		status: submission.status,
		statusChipColor: submissionStatusChipColor(submission.status),
		statusLabelKey: submissionStatusLabelKey(submission.status),
		submissionId: submission.submissionId,
		version: submission.version
	};
};
